import { useEffect, useRef } from "react";

import { adviseFlow } from "./flowAdvice";
import { flowPlan } from "./flowPlan";
import type { FlowDocument } from "./types";

/**
 * What this flow will do on a real call, as an outline, with advice about
 * anything a caller would experience as a fault.
 *
 * A native <dialog> rather than a hand-rolled overlay: showModal() gives
 * focus trapping, Escape-to-close, the top layer and inert background for
 * free, all of which a div would have to reimplement badly.
 *
 * Thin by design -- flowPlan.ts decides the outline and flowAdvice.ts the
 * advice, both pure and unit-tested. This file only renders them, which is
 * why there is no test for it (the repository keeps logic in .ts modules
 * with .test.ts files and components free of it).
 */
export function FlowPlanDialog({
  document: flow,
  title,
  onClose,
}: {
  document: FlowDocument;
  title: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // showModal() rather than the `open` attribute: only the method puts the
    // dialog in the top layer and makes the rest of the page inert.
    if (!node.open) node.showModal();
    return () => {
      if (node.open) node.close();
    };
  }, []);

  const steps = flowPlan(flow);
  const advice = adviseFlow(flow);

  return (
    <dialog
      ref={ref}
      className="plan-dialog"
      aria-labelledby="plan-dialog-heading"
      // Escape fires `cancel`, and the browser closes the dialog itself; the
      // parent still has to be told or it will try to render it again.
      onCancel={onClose}
      onClose={onClose}
    >
      <div className="plan-dialog__head">
        <h2 id="plan-dialog-heading">What this call will do</h2>
        <button type="button" className="btn--outline btn--sm" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="card__note">
        {title} — the steps below are read from the published flow itself, in the order the
        caller will meet them.
      </p>

      {advice.length > 0 ? (
        <div className="plan-dialog__advice" role="note">
          <h3>Worth checking ({advice.length})</h3>
          <ul>
            {advice.map((item) => (
              <li key={`${item.code}:${item.nodeId}:${item.detail}`}>{item.detail}</li>
            ))}
          </ul>
          <p className="card__note">
            These are not errors — the flow is valid and the call will be placed. They are
            places where a caller is likely to think something went wrong.
          </p>
        </div>
      ) : null}

      {steps.length === 0 ? (
        <p>This flow has no reachable steps from its start node.</p>
      ) : (
        <ol className="plan-dialog__steps">
          {steps.map((step, index) => (
            <li
              key={`${step.nodeId}:${index}`}
              style={{ marginInlineStart: `${step.depth * 1.5}rem` }}
            >
              {step.via === "" ? null : <span className="plan-dialog__via">{step.via}</span>}{" "}
              <strong>{step.title}</strong>{" "}
              <span className="plan-dialog__summary">{step.summary}</span>
              {step.revisited ? (
                <span className="plan-dialog__loop"> (continues as shown above)</span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </dialog>
  );
}
